// ========================================
// ERROR PAGE COMPONENT
// ========================================
// Full-page error display for critical errors and error boundaries

import React from 'react'
import { SUPPORT_EMAIL } from '../../../constants/routes'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../card'
import { Button } from '../button'
import { Icon } from '../icon'
import {
  StandardError,
  ErrorSeverity,
  ErrorRecoveryAction,
} from '../../../types/errorTypes'
import {
  faTriangleExclamation,
  faCircleXmark,
  faCircleExclamation,
  faCircleInfo,
  faRotate,
  faPhone,
  faHouse,
  faArrowLeft,
} from '@awesome.me/kit-e215ba4646/icons/sharp/light'

interface ErrorPageProps {
  error?: StandardError
  title?: string
  description?: string
  onRetry?: () => void
  onGoBack?: () => void
  onGoHome?: () => void
  onContactSupport?: () => void
  showTechnicalDetails?: boolean
  children?: React.ReactNode
}

const getSeverityIcon = (severity: ErrorSeverity) => {
  switch (severity) {
    case ErrorSeverity.CRITICAL:
      return faCircleXmark
    case ErrorSeverity.HIGH:
      return faTriangleExclamation
    case ErrorSeverity.MEDIUM:
      return faCircleExclamation
    case ErrorSeverity.LOW:
      return faCircleInfo
    default:
      return faCircleExclamation
  }
}

const getSeverityColor = (severity: ErrorSeverity) => {
  switch (severity) {
    case ErrorSeverity.CRITICAL:
      return 'text-destructive bg-destructive/10 dark:bg-destructive/50'
    case ErrorSeverity.HIGH:
      return 'text-warning bg-warning/10 dark:bg-warning/50'
    case ErrorSeverity.MEDIUM:
      return 'text-warning bg-warning/10 dark:bg-warning/50'
    case ErrorSeverity.LOW:
      return 'text-info bg-info/10 dark:bg-info/50'
    default:
      return 'text-muted-foreground bg-muted'
  }
}

export function ErrorPage({
  error,
  title,
  description,
  onRetry,
  onGoBack,
  onGoHome,
  onContactSupport,
  showTechnicalDetails = false,
  children,
}: ErrorPageProps) {
  const defaultTitle = error
    ? error.severity === ErrorSeverity.CRITICAL
      ? 'Critical System Error'
      : 'Something Went Wrong'
    : 'Error'

  const defaultDescription =
    error?.userMessage || description || 'An unexpected error has occurred.'

  const iconDef = error
    ? getSeverityIcon(error.severity)
    : faTriangleExclamation
  const iconColorClass = error
    ? getSeverityColor(error.severity)
    : 'text-destructive bg-destructive/10 dark:bg-destructive/50'

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    }
    // Always reload as fallback — if onRetry just resets state and the
    // error recurs, the user is stuck. A full reload clears stale modules.
    window.location.reload()
  }

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack()
    } else {
      window.history.back()
    }
  }

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome()
    } else {
      window.location.href = '/'
    }
  }

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport()
    } else {
      // Default support action - could open a modal or navigate to support page
      window.open(
        `mailto:${SUPPORT_EMAIL}?subject=Error Report&body=` +
          encodeURIComponent(
            `Error ID: ${error?.id || 'N/A'}\nTimestamp: ${error?.context.timestamp || new Date().toISOString()}\nURL: ${window.location.href}\n\nDescription: ${defaultDescription}`
          )
      )
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative z-[100]">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center space-y-4">
            {/* Error Icon */}
            <div
              className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${iconColorClass}`}
            >
              <Icon icon={iconDef} className="h-8 w-8" />
            </div>

            {/* Title and Description */}
            <div className="space-y-2">
              <CardTitle className="text-xl">{title || defaultTitle}</CardTitle>
              <CardDescription className="text-base">
                {defaultDescription}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Technical Details (Development Only) */}
            {showTechnicalDetails && import.meta.env.DEV && error && (
              <details className="rounded-lg border bg-muted p-4 text-sm">
                <summary className="cursor-pointer font-medium">
                  Technical Details (Development Only)
                </summary>
                <div className="mt-3 space-y-2 text-xs">
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-medium">ID:</span>
                    <span className="col-span-2 font-mono">{error.id}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-medium">Code:</span>
                    <span className="col-span-2 font-mono">{error.code}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-medium">Category:</span>
                    <span className="col-span-2">{error.category}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-medium">Severity:</span>
                    <span className="col-span-2">{error.severity}</span>
                  </div>
                  {error.technicalMessage && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="font-medium">Technical:</span>
                      <span className="col-span-2">
                        {error.technicalMessage}
                      </span>
                    </div>
                  )}
                  {error.context.component && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="font-medium">Component:</span>
                      <span className="col-span-2">
                        {error.context.component}
                      </span>
                    </div>
                  )}
                  {error.context.timestamp && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="font-medium">Time:</span>
                      <span className="col-span-2 font-mono text-xs">
                        {new Date(error.context.timestamp).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {error.stack && (
                    <div className="mt-3">
                      <div className="font-medium mb-1">Stack Trace:</div>
                      <pre className="text-xs bg-background border rounded p-2 overflow-auto max-h-32">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Custom Content */}
            {children}

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Primary Action — always available */}
              <Button
                onClick={handleRetry}
                className="w-full flex items-center justify-center gap-2"
              >
                <Icon icon={faRotate} className="h-4 w-4" />
                {error?.recoveryAction === ErrorRecoveryAction.RELOAD
                  ? 'Reload Page'
                  : 'Try Again'}
              </Button>

              {/* Secondary Actions */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={handleGoBack}
                  className="flex items-center justify-center gap-2"
                >
                  <Icon icon={faArrowLeft} className="h-4 w-4" />
                  Go Back
                </Button>
                <Button
                  variant="outline"
                  onClick={handleGoHome}
                  className="flex items-center justify-center gap-2"
                >
                  <Icon icon={faHouse} className="h-4 w-4" />
                  Home
                </Button>
              </div>

              {/* Support Action */}
              {(error?.recoveryAction === ErrorRecoveryAction.CONTACT_SUPPORT ||
                error?.severity === ErrorSeverity.CRITICAL ||
                onContactSupport) && (
                <Button
                  variant="outline"
                  onClick={handleContactSupport}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Icon icon={faPhone} className="h-4 w-4" />
                  Contact Support
                </Button>
              )}
            </div>

            {/* Help Text */}
            <div className="text-center text-sm text-muted-foreground">
              {error?.category === 'chunk_load' ? (
                <p>
                  This usually happens after an app update. Reloading should fix
                  the issue.
                </p>
              ) : error?.category === 'network' ? (
                <p>Please check your internet connection and try again.</p>
              ) : error?.severity === ErrorSeverity.CRITICAL ? (
                <p>
                  We apologize for the inconvenience. Our team has been
                  notified.
                </p>
              ) : (
                <p>
                  If this problem continues, please contact our support team.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ErrorPage
